import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPaymentLink, getBrandPaymentLinks, updatePaymentLink } from '../services/paymentLinkService';
import { fetchSubscriptionPlans } from '../features/subscriptionPlansSlice';
import styles from './PaymentLinkModal.module.css';

function PaymentLinkModal({ brand, isOpen, onClose }) {
  const dispatch = useDispatch();
  const { plans } = useSelector((state) => state.subscriptionPlans);
  
  const [currentTab, setCurrentTab] = useState('existing'); // 'existing' or 'create'
  const [existingLinks, setExistingLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Create link form state
  const [formData, setFormData] = useState({
    plan_id: '',
    customer_email: '',
    customer_name: '',
    customer_phone: '',
    expiry_days: 7,
    notes: ''
  });

  const loadExistingLinks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getBrandPaymentLinks(brand.id, { status: 'active' });
      
      console.log('Payment links API response:', response); // Debug log
      
      // Handle the actual API response structure
      let links = [];
      if (response.status === 'success' && response.payment_links) {
        // Backend returns: { status: "success", payment_links: [...] }
        links = response.payment_links;
      } else if (response.data && response.data.results) {
        // Fallback: If response has nested data.results
        links = response.data.results;
      } else if (response.results) {
        // Fallback: If response directly has results
        links = response.results;
      } else if (Array.isArray(response)) {
        // Fallback: If response is directly an array
        links = response;
      } else {
        // Fallback: Empty array
        links = [];
      }
      
      console.log('Parsed payment links:', links); // Debug log
      setExistingLinks(links);
      
      // Auto switch to create tab if no existing links
      if (links.length === 0) {
        setCurrentTab('create');
      }
    } catch (err) {
      console.error('Error loading existing links:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack
      });
      
      // Only show error if it's a real error, not just empty response
      if (err.message && !err.message.includes('404') && !err.message.includes('Not Found')) {
        setError('Failed to load existing payment links');
      }
      
      // Always switch to create tab if loading fails
      setCurrentTab('create');
    } finally {
      setLoading(false);
    }
  }, [brand?.id]);

  useEffect(() => {
    if (isOpen && brand) {
      dispatch(fetchSubscriptionPlans());
      loadExistingLinks();
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, brand, dispatch, loadExistingLinks]);

  const handleCreateLink = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const linkData = {
        brand_id: brand.id,
        ...formData
      };

      const response = await createPaymentLink(linkData);
      
      if (response.status === 'success') {
        setSuccess('Payment link created successfully!');
        
        // Show the new link and switch to existing tab
        await loadExistingLinks();
        setCurrentTab('existing');
        
        // Reset form
        setFormData({
          plan_id: '',
          customer_email: '',
          customer_name: '',
          customer_phone: '',
          expiry_days: 7,
          notes: ''
        });
      }
    } catch (err) {
      console.error('Error creating payment link:', err);
      setError(err.message || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link.shareable_url);
      setSuccess('Payment link copied to clipboard!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link.shareable_url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSuccess('Payment link copied to clipboard!');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDisableLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to disable this payment link?')) {
      return;
    }
    
    try {
      setLoading(true);
      await updatePaymentLink(linkId, { is_active: false });
      setSuccess('Payment link disabled successfully');
      setTimeout(() => setSuccess(null), 3000);
      await loadExistingLinks();
    } catch (err) {
      console.error('Error disabling payment link:', err);
      setError('Failed to disable payment link');
    } finally {
      setLoading(false);
    }
  };

  const generateWhatsAppLink = (link) => {
    const message = `Hi! Here's your payment link for ${brand.name}: ${link.shareable_url}`;
    return `https://wa.me/?text=${encodeURIComponent(message)}`;
  };

  const generateEmailLink = (link) => {
    const subject = `Payment Link for ${brand.name}`;
    const body = `Hi,

Please use the following link to complete your payment for ${brand.name}:

${link.shareable_url}

Thank you!`;
    return `mailto:${link.customer_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Payment Links for {brand.name}</h2>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${currentTab === 'existing' ? styles.active : ''}`}
            onClick={() => setCurrentTab('existing')}
          >
            Existing Links ({existingLinks.length})
          </button>
          <button 
            className={`${styles.tab} ${currentTab === 'create' ? styles.active : ''}`}
            onClick={() => setCurrentTab('create')}
          >
            Create New Link
          </button>
        </div>

        {error && (
          <div className={styles.error}>
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className={styles.success}>
            {success}
            <button onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {currentTab === 'existing' && (
          <div className={styles.existingLinks}>
            {loading ? (
              <div className={styles.loading}>Loading existing links...</div>
            ) : existingLinks.length > 0 ? (
              <div className={styles.linksList}>
                {existingLinks.map((link) => (
                  <div key={link.id} className={styles.linkCard}>
                    <div className={styles.linkInfo}>
                      <div className={styles.linkHeader}>
                        <span className={styles.planName}>{link.plan?.name || 'N/A'}</span>
                        <span className={styles.amount}>₹{link.amount} {link.currency}</span>
                      </div>
                      <div className={styles.customerInfo}>
                        <span>Customer: {link.customer_name} ({link.customer_email})</span>
                        <span>Expires: {new Date(link.expires_at).toLocaleDateString()}</span>
                      </div>
                      {link.notes && (
                        <div className={styles.notes}>
                          <span>Notes: {link.notes}</span>
                        </div>
                      )}
                      <div className={styles.linkUrl}>
                        <input 
                          type="text" 
                          value={link.shareable_url} 
                          readOnly 
                          className={styles.linkInput}
                        />
                      </div>
                    </div>
                    <div className={styles.linkActions}>
                      <button 
                        className={styles.copyBtn}
                        onClick={() => handleCopyLink(link)}
                      >
                        📋 Copy
                      </button>
                      <a 
                        href={generateWhatsAppLink(link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.whatsappBtn}
                      >
                        💬 WhatsApp
                      </a>
                      <a 
                        href={generateEmailLink(link)}
                        className={styles.emailBtn}
                      >
                        📧 Email
                      </a>
                      <button 
                        className={styles.disableBtn}
                        onClick={() => handleDisableLink(link.id)}
                        disabled={loading}
                      >
                        ❌ Disable
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noLinks}>
                <p>No active payment links found for this brand.</p>
                <button 
                  className={styles.createFirstBtn}
                  onClick={() => setCurrentTab('create')}
                >
                  Create First Payment Link
                </button>
              </div>
            )}
          </div>
        )}

        {currentTab === 'create' && (
          <form onSubmit={handleCreateLink} className={styles.createForm}>
            <div className={styles.formGroup}>
              <label>Subscription Plan *</label>
              <select 
                value={formData.plan_id} 
                onChange={(e) => setFormData({...formData, plan_id: e.target.value})}
                required
              >
                <option value="">Select a plan...</option>
                {plans && plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ₹{plan.price_per_month}/month
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Customer Email *</label>
                <input 
                  type="email" 
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  placeholder="customer@example.com"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Customer Name *</label>
                <input 
                  type="text" 
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Customer Phone</label>
                <input 
                  type="tel" 
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  placeholder="+91-9876543210"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Link Expires In</label>
                <select 
                  value={formData.expiry_days}
                  onChange={(e) => setFormData({...formData, expiry_days: parseInt(e.target.value)})}
                >
                  <option value={1}>1 Day</option>
                  <option value={3}>3 Days</option>
                  <option value={7}>1 Week</option>
                  <option value={14}>2 Weeks</option>
                  <option value={30}>1 Month</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Notes (Optional)</label>
              <textarea 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Add any notes about this payment link..."
                rows={3}
              />
            </div>

            <div className={styles.formActions}>
              <button type="button" onClick={onClose} className={styles.cancelBtn}>
                Cancel
              </button>
              <button type="submit" disabled={loading} className={styles.createBtn}>
                {loading ? 'Creating...' : 'Create Payment Link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default PaymentLinkModal;
